import React, { useContext } from 'react';
import { Button, Grid } from '@mui/material';
import type { TypographyProps } from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import {
  ICON_IDS,
  TEXT_CONTENT_IDS,
} from '../../utils/element-ids';
import { CCGThemeProviderContext } from '../../capabilities/contextStore/CCGThemeProviderContext';
import IconRenderer from '../Icons/IconRenderer';
import type { NotificationAlertProps } from '../../shared/types/NotificationTypes';

import { ALERT_ELEMENT } from './NotificationAlertStyleConstant';
import withTitleGuard from './withTitleGuard';

/**
 * CC-918 - Needed to remove aria-hidden=true from icons and reading through the
 * implementation (https://github.com/mui/material-ui/blob/acc033cd56c5b14dcb0cd398292cb4483d0267bd/packages/mui-material/src/SvgIcon/SvgIcon.js)
 * it seems like if you pass "titleAccess" prop to the SVG icons then
 * it won't add the aria-hidden attribute
 */
const getIconDefaults = (
  severity: 'success' | 'warning' | 'error' | 'info',
  id: string | undefined,
) => {
  const iconIdPrefix = id
    ? `notificationAlert${id}Icon`
    : ICON_IDS.NOTIFICATION_ALERT_ICON;

  const iconDefaults = {
    // REMINDER change ids for data test ids in icons
    success: (
      <IconRenderer
        testId={`${iconIdPrefix}-success`}
        name="checkCircleRounded"
        size="inherit"
        aria-label="Success"
        id={`${iconIdPrefix}-success`}
        data-testid={`${iconIdPrefix}-success`}
        titleAccess="Success"
        sx={{ color: ALERT_ELEMENT.success.iconColor }}
      />
    ),
    error: (
      <IconRenderer
        testId={`${iconIdPrefix}-error`}
        name="errorRounded"
        size="inherit"
        aria-label="Error"
        id={`${iconIdPrefix}-error`}
        data-testid={`${iconIdPrefix}-error`}
        titleAccess="Error"
        sx={{ color: ALERT_ELEMENT.error.iconColor }}
      />
    ),
    warning: (
      <IconRenderer
        testId={`${iconIdPrefix}-warning`}
        name="warningNotificationIcon"
        fill={ALERT_ELEMENT.warning.iconColor}
        aria-label="Warning"
        id={`${iconIdPrefix}-warning`}
        data-testid={`${iconIdPrefix}-warning`}
        titleAccess="Warning"
        sx={{ color: ALERT_ELEMENT.warning.iconColor }}
      />
    ),
    info: (
      <IconRenderer
        testId={`${iconIdPrefix}-info`}
        data-testid={`${iconIdPrefix}-info`}
        name="flagRounded"
        size="inherit"
        aria-label="Info"
        id={`${iconIdPrefix}-info`}
        titleAccess="Info"
        sx={{ color: ALERT_ELEMENT.info.iconColor }}
      />
    ),
  };

  return iconDefaults[severity];
};

interface StyledMessageProps extends TypographyProps {
  displayInline?: boolean;
}

const UnwrappedNotificationAlert: React.FC<
  NotificationAlertProps
> = ({
  severity = 'success',
  icon,
  onClose,
  title,
  message,
  inlineMessage = false,
  id,
  wrapperClassName,
  buttonHandler,
  showButton,
  buttonLabel,
}: NotificationAlertProps) => {
  if (showButton && !buttonHandler) {
    console.error(
      'NotificationAlert: The "handleClearSession" prop must be provided when "clearSessionButton" is true.',
    );
  }

  const { appearance } = useContext(CCGThemeProviderContext);

  const messageId = id
    ? `notificationAlert${id}Message`
    : TEXT_CONTENT_IDS.NOTIFICATION_ALERT_MESSAGE;

  const titleId = id
    ? `notificationAlert${id}Title`
    : TEXT_CONTENT_IDS.NOTIFICATION_ALERT_TITLE;

  const containerId = id
    ? `notification${id}Container`
    : TEXT_CONTENT_IDS.NOTIFICATION_CONTAINER;

  const closeButtonId = id
    ? `notificationAlert${id}CloseButton`
    : TEXT_CONTENT_IDS.NOTIFICATION_ALERT_CLOSE_ICON_BUTTON;

  const StyledTitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.body1.fontSize,
    lineHeight: '26px',
    fontWeight: theme.typography.fontWeightMedium,
    color: ALERT_ELEMENT?.[severity]?.titleColor,
    '@media (max-width:539px)': {
      fontWeight: theme.typography.fontWeightMedium,
    },
  }));

  const StyledMessage = styled(Typography)<StyledMessageProps>(
    ({ displayInline, theme }) => ({
      fontSize: theme.typography.body1.fontSize,
      lineHeight: theme.typography.body1.lineHeight,
      fontWeight: theme.typography.fontWeightLight,
      color: ALERT_ELEMENT?.[severity]?.msgColor,
      ...(displayInline && {
        marginLeft: theme.spacing(2),
      }),
    }),
  );

  const StyledAlert = styled(Alert)({
    borderRadius: '12px',
    padding: '14px 12px 14px 16px',
    columnGap: '4px',
    bgcolor: ALERT_ELEMENT?.[severity]?.backgroundColor,
    borderColor: ALERT_ELEMENT?.[severity]?.borderColor,
    ...(appearance?.border?.alertBoxBorderRadius && {
      borderRadius: `${appearance.border.alertBoxBorderRadius} !important`,
    }),
    '& [class$=MuiAlert-icon]': {
      padding: 0,
    },
    '& [class$=MuiAlert-message]': {
      padding: 0,
    },
    '& [class$=MuiAlert-action]': {
      alignItems: 'baseline',
      padding: 0,
    },
    '& [class$=MuiAlert-action] button': {
      padding: 0,
      color: '#424242',
      '&:hover': {
        background: 'transparent',
      },
    },
  });

  const alertMessage = message && (
    <StyledMessage
      id={messageId}
      data-testid={messageId}
      variant="caption"
      displayInline={inlineMessage}
    >
      <span dangerouslySetInnerHTML={{ __html: message }} />
    </StyledMessage>
  );
  const alertTitle = title && (
    <StyledTitle>
      <span
        id={titleId}
        data-testid={titleId}
      >
        <span dangerouslySetInnerHTML={{ __html: title }} />
      </span>
      {inlineMessage && alertMessage}
    </StyledTitle>
  );

  return (
    <StyledAlert
      severity={severity}
      icon={icon || getIconDefaults(severity, id)}
      onClose={onClose}
      id={containerId}
      className={wrapperClassName}
      data-testid={containerId}
      action={
        onClose && (
          <IconButton
            id={closeButtonId}
            data-testid={closeButtonId}
            aria-label="close"
            color="inherit"
            size="small"
            onClick={onClose}
          >
            <IconRenderer
              testId="notification-alert-close-icon"
              name="close"
              size="inherit"
            />
          </IconButton>
        )
      }
    >
      <div style={{ overflow: 'hidden', marginRight: '12px' }}>
        {alertTitle}
        {!inlineMessage && alertMessage}
      </div>

      {showButton ? (
        <Grid style={{ marginTop: '12px' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={buttonHandler}
            id={TEXT_CONTENT_IDS.WARNING_CARD_CANCEL_BUTTON}
          >
            {buttonLabel}
          </Button>
        </Grid>
      ) : null}
    </StyledAlert>
  );
};
const NotificationAlert = withTitleGuard(
  UnwrappedNotificationAlert,
);

export default NotificationAlert;
